/**
 * Per-tenant feature settings and the configurable tax rate.
 *
 * The values live in system_settings, one row per key, so they are per tenant by
 * construction. GET /api/system-settings already turns 'true' and 'false' into
 * booleans and numeric strings into numbers, but the raw strings are accepted
 * here too so a caller never has to care which form it got.
 */
import { BACKEND_URL } from '../config';

// The rate every receipt, ticket and invoice used before the setting existed.
export const DEFAULT_TAX_RATE = 16;

const DEFAULT_FEATURES = {
  tableAreasEnabled: false,
  orderLabelsEnabled: false,
  serviceChargeEnabled: false,
  splitPaymentsEnabled: false,
  customItemsEnabled: false,
  taxRate: DEFAULT_TAX_RATE
};

let cachedFeatures = { ...DEFAULT_FEATURES };
let inFlight = null;

const asBoolean = (value) => value === true || value === 'true';

const asRate = (value) => {
  const rate = parseFloat(value);
  return (!isNaN(rate) && rate >= 0) ? rate : DEFAULT_TAX_RATE;
};

// Last known values. Synchronous, for the plain functions that build receipts.
export const getSystemFeatures = () => cachedFeatures;

export const getTaxRate = () => cachedFeatures.taxRate;

/**
 * Fetch the settings and refresh the cache. Concurrent callers share one
 * request. On any failure the last known values are kept, so a printer never
 * falls back to a different tax rate than the screen is showing.
 */
export const loadSystemFeatures = () => {
  if (inFlight) return inFlight;
  inFlight = fetch(`${BACKEND_URL}/api/system-settings`)
    .then(res => (res.ok ? res.json() : {}))
    .then(data => {
      cachedFeatures = {
        tableAreasEnabled: asBoolean(data.tableAreasEnabled),
        orderLabelsEnabled: asBoolean(data.orderLabelsEnabled),
        serviceChargeEnabled: asBoolean(data.serviceChargeEnabled),
        splitPaymentsEnabled: asBoolean(data.splitPaymentsEnabled),
        customItemsEnabled: asBoolean(data.customItemsEnabled),
        taxRate: asRate(data.taxRate)
      };
      return cachedFeatures;
    })
    .catch(() => cachedFeatures)
    .finally(() => { inFlight = null; });
  return inFlight;
};

// 16 prints as "16" and 8.5 as "8.5", never as "16.00". Rounded to two
// decimals first, so a rate that arrives as 16.000000000000004 from a divide
// and multiply still prints as 16.
export const formatTaxRate = (rate = getTaxRate()) => String(Number(Number(rate).toFixed(2)));

// Label used on receipts and tickets, for example "IVA (16%)".
export const taxLabel = (rate = getTaxRate()) => `IVA (${formatTaxRate(rate)}%)`;

/**
 * Split an inclusive total into the amount before tax and the tax itself.
 * Tax is included in every price, so subtotal = total / (1 + rate/100).
 */
export const splitTaxInclusive = (total, rate = getTaxRate()) => {
  const subtotal = Number(total) / (1 + Number(rate) / 100);
  return { subtotal, tax: Number(total) - subtotal };
};
